import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import ChevronIcon from 'src/assets/chevron-icon';
import useTranslation from 'hooks/use-translation';

import { Benefits } from '../benefits';
import { Faq } from '../faq';
import { HowItWorks } from '../how-it-works';

export const AUTOPLAY_INTERVAL_MS = 30_000;

export function SignupEducationCarousel(): JSX.Element {
  const { t } = useTranslation();
  const autoplayPluginRef = useRef(
    Autoplay({ delay: AUTOPLAY_INTERVAL_MS, stopOnInteraction: false, stopOnMouseEnter: false })
  );
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: 'start', slidesToScroll: 1 }, [
    autoplayPluginRef.current,
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const [isFaqExpanded, setIsFaqExpanded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    (): boolean =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [resumeCount, setResumeCount] = useState(0);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const wasPausedRef = useRef(false);

  const isPaused = isHovered || hasFocusWithin || (isFaqExpanded && selectedIndex === 2) || prefersReducedMotion;

  const isPausedRef = useRef(isPaused);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = (): void => setPrefersReducedMotion(query.matches);
    const changeTarget = (query as unknown) as {
      addEventListener?: (type: string, listener: () => void) => void;
      removeEventListener?: (type: string, listener: () => void) => void;
      addListener?: (listener: () => void) => void;
      removeListener?: (listener: () => void) => void;
    };
    if (changeTarget.addEventListener && changeTarget.removeEventListener) {
      changeTarget.addEventListener('change', update);
      return () => changeTarget.removeEventListener?.('change', update);
    }
    changeTarget.addListener?.(update);
    return () => changeTarget.removeListener?.(update);
  }, []);

  useEffect(() => {
    if (!embla) return undefined;
    const onSelect = (): void => setSelectedIndex(embla.selectedScrollSnap());
    embla.on('select', onSelect);
    embla.on('reInit', onSelect);
    onSelect();
    return () => {
      embla.off('select', onSelect);
      embla.off('reInit', onSelect);
    };
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    if (isPaused) {
      autoplayPluginRef.current.stop();
    } else {
      autoplayPluginRef.current.play();
    }
  }, [embla, isPaused]);

  useEffect(() => {
    if (!embla) return undefined;
    // Autoplay's stop() never clears its timer id, so the plugin's own reset() on
    // pointerUp / visibilitychange restarts the timer even after we stopped it.
    const stopIfPaused = (): void => {
      if (isPausedRef.current) autoplayPluginRef.current.stop();
    };
    // Resize re-activates embla, which re-inits the plugin (playOnInit) and re-registers
    // its listeners after ours, so re-subscribe to run after them again.
    const handleReInit = (): void => {
      embla.off('pointerUp', stopIfPaused);
      embla.on('pointerUp', stopIfPaused);
      document.removeEventListener('visibilitychange', stopIfPaused);
      document.addEventListener('visibilitychange', stopIfPaused);
      stopIfPaused();
    };
    embla.on('pointerUp', stopIfPaused);
    embla.on('reInit', handleReInit);
    document.addEventListener('visibilitychange', stopIfPaused);
    return () => {
      embla.off('pointerUp', stopIfPaused);
      embla.off('reInit', handleReInit);
      document.removeEventListener('visibilitychange', stopIfPaused);
    };
  }, [embla]);

  useEffect(() => {
    if (wasPausedRef.current && !isPaused) {
      setResumeCount((count) => count + 1);
    }
    wasPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    slideRefs.current.forEach((slide, index) => {
      if (!slide) return;
      if (index === selectedIndex) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });
  }, [selectedIndex]);

  const handleMouseEnter = (): void => setIsHovered(true);
  const handleMouseLeave = (): void => setIsHovered(false);
  const handleFocusCapture = (): void => setHasFocusWithin(true);
  const handleBlurCapture = (event: React.FocusEvent<HTMLElement>): void => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    setHasFocusWithin(false);
  };

  const handlePrevious = (): void => {
    if (!embla) return;
    embla.scrollPrev();
  };

  const handleNext = (): void => {
    if (!embla) return;
    embla.scrollNext();
  };

  const handleScrollTo = (index: number): void => {
    if (!embla || index === selectedIndex) return;
    embla.scrollTo(index);
  };

  const slides = [
    { key: 'how-it-works', content: <HowItWorks variant='desktop' /> },
    { key: 'benefits', content: <Benefits variant='desktop' /> },
    { key: 'faq', content: <Faq variant='desktop' onExpandedChange={setIsFaqExpanded} /> },
  ];

  return (
    <Region
      aria-roledescription='carousel'
      aria-label={t('dutchiePay.signup.carousel.label', 'Pay by Bank information')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
    >
      <Viewport ref={emblaRef}>
        <Track>
          {slides.map((slide, index) => (
            <Slide
              key={slide.key}
              ref={(element: HTMLDivElement | null): void => {
                slideRefs.current[index] = element;
              }}
              aria-hidden={selectedIndex !== index}
            >
              {slide.content}
            </Slide>
          ))}
        </Track>
      </Viewport>
      <ControlsRow>
        <NavButton
          type='button'
          aria-label={t('dutchiePay.signup.carousel.previousSlide', 'Previous slide')}
          onClick={handlePrevious}
        >
          <PreviousChevron width={14} height={9} />
        </NavButton>
        <IndicatorGroup role='group' aria-label={t('dutchiePay.signup.carousel.slideControls', 'Slide controls')}>
          {slides.map((slide, index) => (
            <IndicatorButton
              key={slide.key}
              type='button'
              aria-label={t('dutchiePay.signup.carousel.goToSlide', 'Go to slide {{index}}', { index: index + 1 })}
              aria-current={selectedIndex === index ? true : undefined}
              onClick={(): void => handleScrollTo(index)}
            >
              <IndicatorTrack $active={selectedIndex === index}>
                {selectedIndex === index && (
                  <IndicatorFill
                    key={`${selectedIndex}-${resumeCount}`}
                    data-testid='pbb-carousel-indicator-fill'
                    $paused={isPaused}
                    $reducedMotion={prefersReducedMotion}
                  />
                )}
              </IndicatorTrack>
            </IndicatorButton>
          ))}
        </IndicatorGroup>
        <NavButton
          type='button'
          aria-label={t('dutchiePay.signup.carousel.nextSlide', 'Next slide')}
          onClick={handleNext}
        >
          <NextChevron width={14} height={9} />
        </NavButton>
      </ControlsRow>
    </Region>
  );
}

const Region = styled.section`
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Viewport = styled.div`
  overflow: hidden;
`;

const Track = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Slide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  padding: 0 16px;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const NavButton = styled.button`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--dcm-pbb-carousel-button-border-radius, 50%);
  background: var(--dcm-pbb-carousel-button-background, ${({ theme }) => theme.colors.white});
  border: 1px solid var(--dcm-pbb-carousel-button-border-color, ${({ theme }) => theme.colors.grey[85]});
  color: var(--dcm-pbb-carousel-button-color, ${({ theme }) => theme.colors.dutchiePayBlack});
  cursor: pointer;
`;

const PreviousChevron = styled(ChevronIcon)`
  transform: rotate(90deg);
`;

const NextChevron = styled(ChevronIcon)`
  transform: rotate(-90deg);
`;

const IndicatorGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 64px;
`;

const IndicatorButton = styled.button`
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
`;

const IndicatorTrack = styled.span<{ $active: boolean }>`
  position: relative;
  display: block;
  overflow: hidden;
  width: ${({ $active }) => ($active ? '32px' : '8px')};
  height: 8px;
  border-radius: var(--dcm-pbb-carousel-indicator-border-radius, 4px);
  background-color: var(--dcm-pbb-carousel-indicator-track-color, #b7dfc8);
  transition: width 0.2s ease;
`;

const fillAnimation = keyframes`
  from {
    width: 0%;
  }
  to {
    width: 100%;
  }
`;

const IndicatorFill = styled.span<{ $paused: boolean; $reducedMotion: boolean }>`
  position: absolute;
  inset: 0;
  display: block;
  border-radius: var(--dcm-pbb-carousel-indicator-border-radius, 4px);
  background-color: var(--dcm-pbb-carousel-indicator-fill-color, ${({ theme }) => theme.colors.dutchiePayGreen});
  animation: ${fillAnimation} ${AUTOPLAY_INTERVAL_MS}ms linear forwards;
  animation-play-state: ${({ $paused }) => ($paused ? 'paused' : 'running')};

  ${({ $reducedMotion }) =>
    $reducedMotion &&
    css`
      animation: none;
    `}
`;
